import { forwardRef, type ReactNode } from 'react';
import type { View, ViewProps } from 'react-native';
import { useResponsive } from '../../hooks/useResponsive';
import type { SpacingScale } from '../../theme/spacing';
import { Box } from './Box';

interface ContainerProps extends ViewProps {
  children?: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | number;
  padding?: SpacingScale;
  paddingX?: SpacingScale;
  paddingY?: SpacingScale;
  centered?: boolean;
}

const maxWidthValues = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  full: '100%',
} as const;

export const Container = forwardRef<View, ContainerProps>(
  (
    {
      children,
      maxWidth = 'lg',
      padding,
      paddingX = 'md',
      paddingY,
      centered = true,
      style,
      ...rest
    },
    ref,
  ) => {
    const { isMobile, isTablet } = useResponsive();

    const getMaxWidth = () => {
      if (typeof maxWidth === 'number') return maxWidth;
      if (maxWidth === 'full') return '100%';
      return maxWidthValues[maxWidth];
    };

    const getPaddingX = () => {
      if (paddingX) return paddingX;
      if (isMobile) return 'sm';
      if (isTablet) return 'md';
      return 'lg';
    };

    return (
      <Box
        ref={ref}
        width="100%"
        maxWidth={getMaxWidth()}
        alignSelf={centered ? 'center' : undefined}
        padding={padding}
        paddingX={getPaddingX()}
        paddingY={paddingY}
        style={style}
        {...rest}
      >
        {children}
      </Box>
    );
  },
);

Container.displayName = 'Container';
